import { useRequiredContext } from "@redotech/react-util/context";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

export type EscalationStatus = "idle" | "waiting" | "human-active" | "closed";

export interface EscalationState {
  escalationId: string | null;
  intercomConversationId: string | null;
  noticeTopic: string | null;
  status: EscalationStatus;
}

export interface EscalationContextType {
  escalation: EscalationState;
  setEscalation: (escalation: Partial<EscalationState>) => void;
  clearEscalation: () => void;
  triageThreadId: string | null;
  setTriageThreadId: (id: string | null) => void;
}

const defaultState: EscalationState = {
  escalationId: null,
  intercomConversationId: null,
  noticeTopic: null,
  status: "idle",
};

export const EscalationContext = createContext<
  EscalationContextType | undefined
>(undefined);

export function useEscalationContext(): EscalationContextType {
  return useRequiredContext(EscalationContext);
}

interface EscalationProviderProps {
  children: ReactNode;
}

export const EscalationProvider = memo(function EscalationProvider({
  children,
}: EscalationProviderProps) {
  const [escalation, setEscalationState] =
    useState<EscalationState>(defaultState);
  const [triageThreadId, setTriageThreadId] = useState<string | null>(null);

  const setEscalation = useCallback((update: Partial<EscalationState>) => {
    setEscalationState((prev) => ({ ...prev, ...update }));
  }, []);

  const clearEscalation = useCallback(() => {
    setEscalationState(defaultState);
  }, []);

  const contextValue = useMemo(
    () => ({
      escalation,
      setEscalation,
      clearEscalation,
      triageThreadId,
      setTriageThreadId,
    }),
    [
      escalation,
      setEscalation,
      clearEscalation,
      triageThreadId,
      setTriageThreadId,
    ],
  );

  return (
    <EscalationContext.Provider value={contextValue}>
      {children}
    </EscalationContext.Provider>
  );
});
